import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { v4 as uuid } from 'uuid';
import Controller from '@ember/controller';
import type IntlService from 'ember-intl/services/intl';
import {
  Error,
  VerificationCodeRequest,
} from '@square/dashboard-protos/models/squareup/multipass/external';
import { TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';
import focusInput from '@banking/savings-onboarding-app/utils/focus-input';
import MultipassExternalApi from '@banking/api/services/multipass-external-api';
import type RouterService from '@ember/routing/router-service';
import { feature } from '@square/ember-features/macros';
import { assertInstanceOf } from '@banking/ui/utils/assert';

const INPUT_SELECTOR = '[data-test-two-factor-phone-number-code-input]';
const PASSCODE_LENGTH = 6;
const VERIFICATION_REGEX = /^\d{6,}$/;

type MarketCodeInputEvent = CustomEvent<{ code: string }>;

export default class TwoFactorSMSController extends Controller {
  @service intl!: IntlService;
  @service multipassExternalApi!: MultipassExternalApi;
  @service router!: RouterService;

  @tracked isLoading = false;
  @tracked showValidation = false;
  @tracked isSendingCode = false;
  @tracked serverError = false;
  @tracked verificationCode = '';
  @tracked passcodeLength = PASSCODE_LENGTH;

  @feature('banking/stop_prefilling_savings_onboarding')
  declare stopPrefilling: boolean;

  get errorMessage(): string | undefined {
    if (!this.showValidation) {
      return undefined;
    } else {
      return this.intl.t('sfs.signup.twoFactor.sms.validation');
    }
  }

  get clientInvalid() {
    return (
      !this.verificationCode || !VERIFICATION_REGEX.test(this.verificationCode)
    );
  }

  @action async marketCodeInputValueChange(event: any) {
    const code = assertInstanceOf<MarketCodeInputEvent>(event, CustomEvent)
      .detail.code;

    this.verificationCode = code;

    if (this.verificationCode.length === PASSCODE_LENGTH) {
      await this._submit();
    }
  }

  @action async resend(event: Event): Promise<void> {
    event.preventDefault();
    this.isSendingCode = true;
    try {
      await this.multipassExternalApi.sendVerificationCode({
        reason: VerificationCodeRequest.Reason.TWO_FACTOR,
        smsPhone: this.model.smsTwoFactor.sms.phone,
        locale: this.intl.primaryLocale,
        idempotenceKey: uuid(),
      });
      focusInput(INPUT_SELECTOR);
    } catch (error) {
      this.send('handleRpcError', error);
    } finally {
      this.isSendingCode = false;
    }
  }

  @action async submit(event: Event): Promise<void> {
    event.preventDefault();
    await this._submit();
  }

  async _submit(): Promise<void> {
    if (this.clientInvalid) {
      this.showValidation = true;
      focusInput(INPUT_SELECTOR);
    } else {
      this.showValidation = false;
      this.isLoading = true;
      try {
        const twoFactorDetails = {
          sms: {
            phone: this.model.smsTwoFactor.sms.phone,
            verificationCode: this.verificationCode,
          },
        } as TwoFactorDetails;

        let multipassError: Error;
        if (this.model.isEnrolling) {
          multipassError = await this.enrollTwoFactor(twoFactorDetails);
        } else {
          multipassError = await this.upgradeSessionTwoFactor(twoFactorDetails);
        }

        if (multipassError === Error.NO_ERROR) {
          if (this.stopPrefilling) {
            this.router.transitionTo('signup.enter-identity-info');
          } else {
            this.router.transitionTo('signup.confirm-identity');
          }
        } else {
          this.handleError(multipassError);
        }
      } catch (error) {
        this.send('handleRpcError', error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  handleError(multipassError: Error) {
    if (multipassError === Error.INVALID_CREDENTIALS) {
      this.serverError = true;
      this.showValidation = true;
      focusInput(INPUT_SELECTOR);
    } else {
      this.router.transitionTo('signup.errored');
    }
  }

  async enrollTwoFactor(twoFactorDetails: TwoFactorDetails): Promise<Error> {
    const response = await this.multipassExternalApi.enrollTwoFactor({
      twoFactorDetails,
      idempotenceKey: uuid(),
    });

    return response.error;
  }

  async upgradeSessionTwoFactor(
    twoFactorDetails: TwoFactorDetails
  ): Promise<Error> {
    const response =
      await this.multipassExternalApi.upgradeSessionTwoFactorRequest(
        twoFactorDetails
      );

    return response.error;
  }
}
